import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Session } from '@/api/client';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import {
  SessionCancelReasonField,
  type SessionCancelReasonFieldHandle,
} from '@/components/SessionDialog/SessionCancelReasonField';
import { Button } from '@/components/ui/Button/Button';
import { Switch } from '@/components/ui/Switch/Switch';
import { Modal } from '@/components/ui/Modal/Modal';
import { ProjectCombo } from '@/components/ProjectCombo/ProjectCombo';
import { ReaderCombo, type ReaderValueItem } from '@/components/ReaderCombo/ReaderCombo';
import { SoundEngineerCombo, type SoundEngineerValueItem } from '@/components/SoundEngineerCombo/SoundEngineerCombo';
import { buildTimeFromMinutes, parseTimeToMinutes, validateSessionTimes, normalizeTimeToHHmm } from '@/schedule/scheduleUtils';
import { autoFontSizePatchForDuration, sessionDurationMinutes } from '@/schedule/sessionShortDurationFontSize';
import type { GridSelection } from '@/schedule/scheduleTypes';
import { getBaseStatus } from '@/utils/projectStatuses';
import { useToast } from '@/contexts/ToastContext';
import { TimeInput } from '@/components/SessionDialog/TimeInput';
import './SessionModal.css';

/** Если задано, в модалке показываются только эти типы сессии (переключатель). */
export type SessionModalAllowedType = 'запись' | 'правки' | 'монтаж' | 'заметка';

type SessionUpdatePayload = Parameters<typeof api.sessions.update>[1];
type SessionCreatePayload = Parameters<typeof api.sessions.create>[0];

interface SessionModalProps {
  open: boolean;
  onClose: () => void;
  /** При использовании мутаций — оптимистичное обновление */
  onUpdateSession?: (vars: { id: string; data: SessionUpdatePayload }) => Promise<Session>;
  onCreateSession?: (data: SessionCreatePayload) => Promise<Session>;
  /** Альтернатива: после API вызывается onSaved (ProjectsTable) */
  onSaved?: (session: Session) => void;
  selection: GridSelection | null;
  editingSession: Session | null;
  /** Ограничить переключатель типов сессии (например, только запись и правки). */
  allowedSessionTypes?: SessionModalAllowedType[];
  /** @deprecated Не используется */
  projects?: unknown[];
}

const DEFAULT_DURATION_SLOTS = 2;

const ALL_SESSION_TYPES: SessionModalAllowedType[] = ['запись', 'правки', 'монтаж', 'заметка'];

export function SessionModal({
  open,
  onClose,
  onUpdateSession,
  onCreateSession,
  onSaved,
  selection,
  editingSession,
  projects: _projects,
  allowedSessionTypes = ALL_SESSION_TYPES,
}: SessionModalProps) {
  const [sessionType, setSessionType] = useState<Session['session_type']>('запись');
  const [sessionSubtype, setSessionSubtype] = useState<Session['session_subtype']>(null);
  const [projectId, setProjectId] = useState('');
  const [readerValue, setReaderValue] = useState<ReaderValueItem[]>([]);
  const [isRecorded, setIsRecorded] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);
  const [canceledNote, setCanceledNote] = useState('');
  const [stopPage, setStopPage] = useState<string>('');
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [soundengineerValue, setSoundengineerValue] = useState<SoundEngineerValueItem | null>(null);
  const [startTimeStr, setStartTimeStr] = useState('');
  const [endTimeStr, setEndTimeStr] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchFlushEpoch, setSearchFlushEpoch] = useState(0);
  const { showError, showSuccess } = useToast();
  const cancelReasonFieldRef = useRef<SessionCancelReasonFieldHandle | null>(null);

  const readerIdFromValue = readerValue[0] && 'id' in readerValue[0] ? readerValue[0].id : null;

  useEffect(() => {
    if (open) setSearchFlushEpoch((n) => n + 1);
  }, [open]);

  const { data: selectedProject, isLoading: isProjectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.projects.get(projectId),
    enabled: !!projectId,
    staleTime: 60_000,
  });

  const { data: projectsForReaderRaw, isLoading: isReaderProjectsLoading } = useQuery({
    queryKey: ['projects', 'reader', readerIdFromValue ?? ''],
    queryFn: async () => {
      const res = await api.projects.list({ readerId: readerIdFromValue ?? undefined, limit: 25, offset: 0 });
      return Array.isArray(res) ? res : res.items;
    },
    enabled: !!readerIdFromValue,
    staleTime: 30_000,
  });
  const projectsForReader = useMemo(() => projectsForReaderRaw ?? [], [projectsForReaderRaw]);

  const projectReaders = useMemo(() => selectedProject?.readers ?? [], [selectedProject?.readers]);
  const isPodcastRecording = sessionType === 'запись' && sessionSubtype === 'podcast';

  const date = editingSession?.date ?? selection?.date ?? '';
  const studioIdx = editingSession
    ? (() => {
        const m = (editingSession.studio || '1').match(/\d+/);
        return Math.max(0, (m ? parseInt(m[0], 10) : 1) - 1);
      })()
    : selection?.studioIdx ?? 0;
  const slotIdx = selection?.slotIdx ?? 0;
  const endSlotIdx = selection?.endSlotIdx ?? selection?.slotIdx ?? slotIdx + DEFAULT_DURATION_SLOTS - 1;

  const startMins = editingSession
    ? parseTimeToMinutes(editingSession.start as string)
    : slotIdx * 30;
  const endMins = editingSession
    ? parseTimeToMinutes(editingSession.end as string)
    : Math.min((endSlotIdx + 1) * 30, 12 * 60);
  const defaultStartTime = buildTimeFromMinutes(startMins);
  const defaultEndTime = buildTimeFromMinutes(endMins);

  useEffect(() => {
    if (!open) return;
    setStartTimeStr(buildTimeFromMinutes(startMins));
    setEndTimeStr(buildTimeFromMinutes(endMins));
    if (editingSession) {
      const rawType = ((editingSession.session_type as Session['session_type']) ?? 'запись') as SessionModalAllowedType;
      const nextType = allowedSessionTypes.includes(rawType) ? rawType : allowedSessionTypes[0];
      setSessionType(nextType);
      setSessionSubtype(editingSession.session_subtype ?? null);
      setProjectId(editingSession.project_id ?? '');
      setReaderValue(
        editingSession.readers?.length
          ? editingSession.readers.map((r) => ({ id: r.id, name: r.name }))
          : editingSession.reader_id && editingSession.reader_name
            ? [{ id: editingSession.reader_id, name: editingSession.reader_name }]
            : []
      );
      setIsRecorded(editingSession.is_recorded ?? false);
      const cancelAllowed = nextType === 'запись' || nextType === 'правки';
      setIsCanceled(cancelAllowed ? (editingSession.is_canceled ?? false) : false);
      setCanceledNote(cancelAllowed ? (editingSession.canceled_note ?? '') : '');
      setStopPage(editingSession.stop_page != null ? String(editingSession.stop_page) : '');
      setNoteTitle(editingSession.title ?? '');
      setNoteContent(editingSession.content ?? '');
      setSoundengineerValue(
        editingSession.soundengineer_id && editingSession.soundengineer_name
          ? { id: editingSession.soundengineer_id, name: editingSession.soundengineer_name, color: editingSession.soundengineer_color }
          : null
      );
    } else {
      setSessionType('запись');
      setSessionSubtype(null);
      setProjectId('');
      setReaderValue([]);
      setIsRecorded(false);
      setIsCanceled(false);
      setCanceledNote('');
      setStopPage('');
      setNoteTitle('');
      setNoteContent('');
      setSoundengineerValue(null);
    }
  }, [open, editingSession, startMins, endMins, allowedSessionTypes]);

  const handleProjectChange = (newProjectId: string, project?: { readers?: { id: string; name: string }[]; project_type?: string | null }) => {
    setProjectId(newProjectId);
    const nextReaders = project?.readers ?? [];
    if (newProjectId && project?.project_type === 'Podcast') {
      setSessionSubtype('podcast');
      setReaderValue(nextReaders.map((r) => ({ id: r.id, name: r.name })));
    } else {
      setSessionSubtype(null);
      if (newProjectId && nextReaders.length === 1) {
        const r = nextReaders[0];
        setReaderValue([{ id: r.id, name: r.name }]);
      } else {
        setReaderValue((prev) => prev.slice(0, 1));
      }
    }
  };

  const handleReaderChange = async (readers: ReaderValueItem[]) => {
    const next = isPodcastRecording ? readers : readers.length > 0 ? [readers[readers.length - 1]] : [];
    setReaderValue(next);
    if (!isPodcastRecording && next.length > 0) {
      const r = next[0];
      if ('id' in r) {
        const res = await api.projects.list({ readerId: r.id, limit: 25, offset: 0 });
        const list = Array.isArray(res) ? res : res.items;
        const filtered = list.filter((p) => getBaseStatus(p.state) !== 'Готов');
        if (filtered.length === 1) {
          setProjectId(filtered[0].id);
        }
      }
    }
  };

  useEffect(() => {
    // Пропускаем пока проект ещё загружается — иначе пустой projectReaders
    // сбросит чтеца, выбранного при инициализации формы редактирования.
    if (isProjectLoading) return;
    if (projectId && readerValue.length > 0) {
      const allowed = new Set(projectReaders.map((pr) => pr.id));
      const next = readerValue.filter((r) => 'id' in r && allowed.has(r.id));
      if (next.length !== readerValue.length) setReaderValue(next);
    }
  }, [projectId, projectReaders, readerValue, isProjectLoading]);

  useEffect(() => {
    // Пропускаем пока проекты чтеца ещё загружаются — иначе пустой список
    // сбросит project_id, выбранный при инициализации формы редактирования.
    // Сверяем с полным списком readerId, а не только «не Готов»: иначе при редактировании
    // сессии с завершённым проектом project_id обнулялся.
    if (isReaderProjectsLoading) return;
    if (readerIdFromValue && projectId && !projectsForReader.some((p) => p.id === projectId)) {
      setProjectId('');
    }
  }, [readerIdFromValue, projectId, projectsForReader, isReaderProjectsLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const canCancelSessionType = sessionType === 'запись' || sessionType === 'правки';
    if (editingSession && isCanceled && canCancelSessionType && cancelReasonFieldRef.current) {
      if (!cancelReasonFieldRef.current.validate()) {
        showError('Укажите причину');
        return;
      }
    }
    const isRecordingType = sessionType === 'запись' || sessionType === 'правки';
    const needsProject = sessionType === 'монтаж';
    if (needsProject && !projectId.trim()) {
      showError('Выберите проект');
      return;
    }
    const resolvedReaderIds = isRecordingType ? readerValue.filter((r) => 'id' in r).map((r) => r.id) : [];
    setSaving(true);
    try {
      const studio = String(studioIdx + 1);
      const rawStart = startTimeStr || defaultStartTime;
      const rawEnd = endTimeStr || defaultEndTime;
      const start = rawStart.length === 5 ? rawStart : normalizeTimeToHHmm(rawStart);
      const end = rawEnd.length === 5 ? rawEnd : normalizeTimeToHHmm(rawEnd);
      const validation = validateSessionTimes(start, end);
      if (!validation.ok) {
        showError(validation.error);
        setSaving(false);
        return;
      }
      const needsAutoFont =
        !editingSession ||
        (() => {
          const ps = String(editingSession.start).slice(0, 5);
          const pe = String(editingSession.end).slice(0, 5);
          const durationChanged =
            sessionDurationMinutes(ps, pe) !== sessionDurationMinutes(start, end);
          const typeChanged =
            String(editingSession.session_type ?? '').trim() !== String(sessionType ?? '').trim();
          return durationChanged || typeChanged;
        })();
      const shortFont = needsAutoFont ? autoFontSizePatchForDuration(start, end, sessionType ?? 'запись') : null;
      const resolvedProjectId = sessionType === 'заметка' ? null : projectId.trim() || null;
      const payload: SessionCreatePayload = {
        date,
        start,
        end,
        studio,
        session_type: sessionType ?? 'запись',
        session_subtype: isPodcastRecording ? 'podcast' : null,
        project_id: resolvedProjectId,
        project_name: resolvedProjectId
          ? (selectedProject?.name ?? editingSession?.project_name ?? null)
          : null,
        project_type: resolvedProjectId
          ? (selectedProject?.project_type ?? editingSession?.project_type ?? null)
          : null,
        reader_ids: isPodcastRecording ? resolvedReaderIds : resolvedReaderIds.slice(0, 1),
        is_recorded: isRecordingType ? isRecorded : false,
        is_partial: false,
        stop_page: sessionType === 'запись' && !isPodcastRecording && stopPage.trim() ? parseInt(stopPage, 10) : null,
        title: sessionType === 'заметка' ? noteTitle.trim() || null : null,
        content: sessionType === 'заметка' ? noteContent.trim() || null : null,
        soundengineer_id: sessionType === 'заметка' ? null : soundengineerValue?.id ?? null,
        soundengineer_name: sessionType === 'заметка' ? null : soundengineerValue?.name ?? null,
        soundengineer_color: sessionType === 'заметка' ? null : soundengineerValue?.color ?? null,
        ...(shortFont ?? {}),
        ...(isEdit
          ? {
              is_canceled: canCancelSessionType ? isCanceled : false,
              canceled_note:
                canCancelSessionType && isCanceled ? canceledNote.trim().slice(0, 300) || null : null,
            }
          : {}),
      };
      if (onUpdateSession && onCreateSession) {
        if (editingSession) {
          await onUpdateSession({ id: editingSession.id, data: payload });
        } else {
          await onCreateSession(payload);
        }
        onClose();
      } else if (onSaved) {
        let session: Session;
        if (editingSession) {
          session = await api.sessions.update(editingSession.id, payload);
          showSuccess('Сессия обновлена');
        } else {
          session = await api.sessions.create(payload);
          showSuccess('Сессия создана');
        }
        onSaved(session);
        onClose();
      }
    } catch (err) {
      showError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const isEdit = !!editingSession;
  const canSubmit =
    sessionType === 'заметка' ||
    (sessionType === 'монтаж' && projectId) ||
    (isPodcastRecording && projectId && readerValue.length > 0) ||
    (sessionType === 'запись' && !isPodcastRecording && (projectId || readerIdFromValue)) ||
    (sessionType === 'правки' && (projectId || readerIdFromValue));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Редактировать сессию' : 'Добавить сессию'}
      headerVariant="island"
    >
      <form id="session-modal-form" onSubmit={handleSubmit} className="form session-modal__form">
        <div className="session-modal__form-body">
            <div className="session-modal__time-row">
              <TimeInput
                label="Начало"
                value={startTimeStr}
                onChange={setStartTimeStr}
                placeholder="09:00"
                aria-label="Время начала"
              />
              <TimeInput
                label="Окончание"
                value={endTimeStr}
                onChange={setEndTimeStr}
                placeholder="12:00"
                aria-label="Время окончания"
              />
            </div>
            <div className="session-modal__type-row">
              {allowedSessionTypes.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`session-modal__type-btn ${sessionType === t ? `session-modal__type-btn_active session-modal__type-btn_${t}` : ''}`}
                  onClick={() => {
                    setSessionType(t);
                    if (t !== 'запись' && t !== 'правки') {
                      setIsCanceled(false);
                      setCanceledNote('');
                    }
                    if (t === 'заметка') {
                      setProjectId('');
                      setReaderValue([]);
                      setSessionSubtype(null);
                      setStopPage('');
                      setIsRecorded(false);
                      setSoundengineerValue(null);
                    }
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
            {(sessionType === 'запись' || sessionType === 'правки') && (
              <>
                <div className="session-modal__project-row">
                <ProjectCombo
                  value={projectId}
                  onChange={handleProjectChange}
                  placeholder="Введите название для поиска"
                  fullWidth
                  readerIdFilter={readerIdFromValue ?? undefined}
                  excludeState="Готов"
                  debounceFlushKey={searchFlushEpoch}
                  initialProject={
                    editingSession?.project_id && editingSession?.project_name
                      ? { id: editingSession.project_id, name: editingSession.project_name }
                      : undefined
                  }
                />
                <div className="form__field form__field_full">
                  <ReaderCombo
                  value={readerValue}
                  onChange={handleReaderChange}
                  placeholder={isPodcastRecording ? 'Чтецы' : 'Чтец'}
                  readersFilter={isPodcastRecording ? projectReaders : projectId ? projectReaders : undefined}
                  multiple={isPodcastRecording}
                  disabled={isPodcastRecording && isProjectLoading}
                />
                </div>
                </div>
                {isEdit && (
                  <>
                    <div className="session-modal__checkboxes-row">
                      <Switch
                        className="form__label session-modal__checkbox-label"
                        checked={isRecorded}
                        onCheckedChange={setIsRecorded}
                        label="Записана"
                      />
                      <Switch
                        className="form__label session-modal__checkbox-label"
                        checked={isCanceled}
                        onCheckedChange={(v) => {
                          setIsCanceled(v);
                          if (!v) setCanceledNote('');
                        }}
                        label="Отменена"
                      />
                    </div>
                    {isCanceled && (
                      <SessionCancelReasonField
                        ref={cancelReasonFieldRef}
                        value={canceledNote}
                        onChange={setCanceledNote}
                      />
                    )}
                    {sessionType === 'запись' && !isPodcastRecording && (
                      <Input
                        value={stopPage}
                        onChange={(e) => setStopPage(e.target.value.replace(/[^\d]/g, ''))}
                        placeholder="Прочитано до"
                        fullWidth
                      />
                    )}
                  </>
                )}
              </>
            )}
            {sessionType === 'монтаж' && (
              <>
                <ProjectCombo
                  value={projectId}
                  onChange={handleProjectChange}
                  placeholder="Введите название для поиска"
                  fullWidth
                  required
                  readerIdFilter={readerIdFromValue ?? undefined}
                  excludeState="Готов"
                  debounceFlushKey={searchFlushEpoch}
                  initialProject={
                    editingSession?.project_id && editingSession?.project_name
                      ? { id: editingSession.project_id, name: editingSession.project_name }
                      : undefined
                  }
                />
              </>
            )}
            {sessionType === 'заметка' && (
              <div className="session-modal__note-row">
                <Input
                  value={noteTitle}
                  onChange={(e) => setNoteTitle(e.target.value)}
                  placeholder="Заголовок"
                  fullWidth
                />
                <Textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  placeholder="Заметка"
                  fullWidth
                  minRows={1}
                  maxRows={4}
                  className="session-modal__note-field"
                  inputClassName="session-modal__note-textarea"
                />
              </div>
            )}
            {sessionType !== 'заметка' && (
              <div className="form__field form__field_full">
                <SoundEngineerCombo
                  value={soundengineerValue}
                  onChange={setSoundengineerValue}
                  placeholder="Звукорежиссёр"
                  fullWidth
                />
              </div>
            )}
          </div>
        <div className="form__footer form__footer_modal">
          <Button variant="outlined" type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" form="session-modal-form" variant="primary" disabled={saving || !canSubmit}>
            {saving ? 'Сохранение…' : isEdit ? 'Сохранить' : 'Добавить'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
